import { LOCATIONS, type Location } from "@/content/locations";

/**
 * Great-circle distance in km. Used only for ranking nearby cities, so the
 * spherical-earth approximation is more than accurate enough.
 */
export function distanceKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

export type NearbyCity = { loc: Location; km: number; sameRegion: boolean };

/**
 * Cities closest to `slug`, ranked by real distance with a bonus for same-division
 * neighbours. Contextual (geographically meaningful) internal links beat an
 * arbitrary `slice(0, 6)` for both users and crawl-depth distribution.
 */
export function nearbyCities(slug: string, count = 6): NearbyCity[] {
  const origin = LOCATIONS.find((l) => l.slug === slug);
  if (!origin) return [];
  return LOCATIONS.filter((l) => l.slug !== slug)
    .map((l) => ({
      loc: l,
      km: Math.round(distanceKm(origin.geo, l.geo)),
      sameRegion: l.region === origin.region,
    }))
    .sort((a, b) => (a.km + (a.sameRegion ? -40 : 0)) - (b.km + (b.sameRegion ? -40 : 0)))
    .slice(0, count);
}

/** Human label for a nearby-city card, e.g. "Rangpur Division · 48 km away". */
export function nearbyLabel(n: NearbyCity): string {
  return `${n.loc.region} · ${n.km} km away`;
}
